import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, ArrowRight, ShieldAlert, GanttChartSquare, Stamp, Gauge, ShieldCheck, Check, CreditCard } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";
import { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { VideraLogo } from "@/components/VideraLogo";

const SELLING_POINTS = [
  { icon: ShieldAlert, title: "Registers that hold up", body: "Risks, issues, decisions and changes — owned, scored, and carrying their own history." },
  { icon: GanttChartSquare, title: "Plans, not task boards", body: "Critical path, dependencies and baselines, so slippage shows against what you actually committed to." },
  { icon: Stamp, title: "Gates that actually hold", body: "Named approvers, routed sign-off, and a trail on every gate, change, decision and report." },
  { icon: Gauge, title: "Governance you can score", body: "Every project measured against your own framework, rolled up to portfolio level." },
  { icon: ShieldCheck, title: "AI inside your own tenant", body: "Point it at your Azure OpenAI resource — or switch it off and lose nothing else." },
];


// The demo login hint is a development convenience. Never show it on a customer-facing domain.
const SHOW_DEMO_HINT = /(^localhost$|^127\.0\.0\.1$|\.preview\.emergentagent\.com$)/.test(
  window.location.hostname
);

export default function Login() {
  const { login } = useAuth();
  const { branding } = useBranding();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  // Only ever an internal path, so a crafted ?next= cannot bounce anyone off-site after signing in.
  const raw = params.get("next") || "";
  const next = raw.startsWith("/") && !raw.startsWith("//") ? raw : "";
  const buying = next.includes("buy=1");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await login(email, password);
      navigate(next || "/dashboard");
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="marketing-root grid min-h-screen grid-cols-1 lg:grid-cols-2">
      {/* Selling panel */}
      <div
        data-testid="login-selling-panel"
        className="grain-overlay relative hidden flex-col justify-between overflow-hidden p-12 text-white lg:flex lg:p-16"
        style={{ backgroundColor: branding.primary_color || "#1F4E79" }}
      >
        <div className="relative z-10 flex items-center justify-between">
          <Link to="/" aria-label={branding.product_name}>
            {branding.logo_url ? (
              <span className="font-marketing-display text-lg font-black tracking-tight text-white">
                {branding.product_name}
              </span>
            ) : (
              <VideraLogo height={26} tone="light" />
            )}
          </Link>
          <Link
            to="/pricing"
            data-testid="login-pricing-link"
            className="text-xs font-semibold uppercase tracking-[0.15em] text-white/60 transition-colors hover:text-white"
          >
            Pricing
          </Link>
        </div>

        <div className="relative z-10 max-w-xl">
          <h1 className="font-marketing-display text-4xl font-black leading-none tracking-tight text-white lg:text-5xl">
            Know where you stand.
            <br />
            Prove how you got there.
          </h1>
          <p className="mt-6 max-w-md text-sm leading-relaxed text-white/70">
            The plan, the risks, the decisions and the reporting as one record — current on Monday
            morning, and still defensible a year later.
          </p>

          <ul className="mt-10 space-y-5">
            {SELLING_POINTS.map((p) => (
              <li key={p.title} className="flex gap-4" data-testid={`login-benefit-${p.title.split(" ")[0].toLowerCase()}`}>
                <p.icon size={18} strokeWidth={1.5} className="mt-0.5 shrink-0 text-white/70" />
                <div>
                  <p className="text-sm font-semibold text-white">{p.title}</p>
                  <p className="mt-0.5 text-xs leading-relaxed text-white/60">{p.body}</p>
                </div>
              </li>
            ))}
          </ul>

          <div className="mt-10 flex flex-wrap items-center gap-3">
            <Link
              to="/signup"
              data-testid="login-trial-cta"
              className="inline-flex items-center gap-2 border border-white/30 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-white hover:text-slate-900"
            >
              Start 7-day free trial <ArrowRight size={15} strokeWidth={1.5} />
            </Link>
            <Link
              to="/pricing"
              data-testid="login-buy-cta"
              className="inline-flex items-center gap-2 bg-white px-5 py-3 text-sm font-semibold text-slate-900 transition-colors hover:bg-slate-200"
            >
              <CreditCard size={15} strokeWidth={1.5} /> Buy now
            </Link>
          </div>
          <p className="mt-3 flex items-center gap-1.5 text-xs text-white/50">
            <Check size={12} strokeWidth={2} /> No card required for the trial. One trial per organisation.
          </p>
        </div>

        <div className="relative z-10 text-[11px] uppercase tracking-[0.2em] text-white/35">{branding.org_name}</div>
      </div>

      {/* Sign-in */}
      <div className="flex flex-1 items-center justify-center bg-white px-6 py-16">
        <form onSubmit={submit} data-testid="login-form" className="w-full max-w-sm stagger-in">
          <Link to="/" className="lg:hidden" aria-label={branding.product_name}>
            {branding.logo_url ? (
              <span className="font-marketing-display text-base font-black tracking-tight">{branding.product_name}</span>
            ) : (
              <VideraLogo height={24} />
            )}
          </Link>
          <h2 className="mt-6 font-marketing-display text-3xl font-black tracking-tight text-slate-900 lg:mt-0">Sign in</h2>
          <p className="mt-2 text-sm text-slate-500">
            {buying ? "Sign in and we will take you straight to checkout." : "Everything is where you left it."}
          </p>

          <div className="mt-8 space-y-4">
            <div>
              <label className="field-label">Email</label>
              <Input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@company.com"
              />
            </div>
            <div>
              <label className="field-label">Password</label>
              <Input
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
              />
            </div>
          </div>

          {error && (
            <div data-testid="login-error" className="mt-4 rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
              {error}
            </div>
          )}

          <Button
            data-testid="login-submit-button"
            type="submit"
            disabled={loading}
            className="mt-6 w-full gap-2"
            style={{ backgroundColor: branding.primary_color }}
          >
            {loading ? <Loader2 className="animate-spin" size={16} /> : <ArrowRight size={16} />}
            Sign in
          </Button>

          <p className="mt-6 text-center text-sm text-slate-500">
            New to {branding.product_name}?{" "}
            <Link to={buying ? `/signup${next.slice(next.indexOf("?"))}` : "/signup"} data-testid="login-signup-link" className="font-semibold text-[color:var(--marketing-primary)] transition-colors hover:underline">
              Start a free trial
            </Link>{" "}
            or{" "}
            <Link to="/pricing" data-testid="login-buy-link" className="font-semibold text-[color:var(--marketing-primary)] transition-colors hover:underline">
              buy a plan now
            </Link>
          </p>

          {SHOW_DEMO_HINT && (
            <div data-testid="login-demo-hint" className="mt-8 rounded border border-slate-200 bg-slate-50 p-3 text-[11px] leading-relaxed text-slate-500">
              <span className="font-semibold text-slate-700">Demo admin</span>
              <br />
              admin@videradelivery.co.uk / VideraDemo2026!
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
